<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-0 p-[20px] md:p-[25px] rounded-md h-full flex flex-col"
  >
    <div class="trezo-card-header mb-[20px] flex items-center justify-between">
      <div class="trezo-card-title">
        <h5 class="mb-0 text-md font-medium">{{ directT('chat.messages') }}</h5>
      </div>
    </div>
    <div class="trezo-card-content flex-grow flex flex-col">
      <form class="relative mb-[20px]">
        <label
          class="absolute ltr:left-[13px] rtl:right-[13px] mt-[2px] text-black dark:text-white top-1/2 -translate-y-1/2"
        >
          <i class="material-symbols-outlined !text-lg"> search </i>
        </label>
        <input
          type="text"
          class="block w-full rounded-md text-black dark:text-white bg-gray-50 dark:bg-[#15203c] border border-gray-50 dark:border-[#15203c] focus:border-primary-500 h-[40px] outline-0 transition-all text-xs placeholder:text-gray-500 dark:placeholder:text-gray-400 ltr:pl-[38px] rtl:pr-[38px] ltr:pr-[15px] rtl:pl-[15px]"
          :placeholder="directT('chat.search')"
        />
      </form>

      <div class="flex-grow">
        <div class="chat-users-list overflow-y-scroll chat-sidebar-height ltr:-mr-[5px] rtl:-ml-[5px] md:ltr:-mr-[10px] md:rtl:-ml-[10px] ltr:pr-[20px] rtl:pl-[20px] md:ltr:pr-[25px] md:rtl:pl-[25px]">
          <div
            v-for="(chat, index) in chatList"
            :key="index"
            class="flex items-center justify-between mb-[13.5px] pb-[13.5px] border-b border-gray-100 dark:border-[#172036] last:border-0 last:pb-0 last:mb-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#15203c] transition-colors p-2 rounded-md"
            @click="selectChat(chat)"
              >
                <div class="flex items-center">
                  <div class="relative ltr:mr-[10px] rtl:ml-[10px]">
                    <img
                  :src="chat.profile_picture_url || defaultProfileImage"
                      alt="user-image"
                      class="rounded-full w-[35px]"
                    />
                    <span
                  class="absolute ltr:right-0 rtl:left-0 bottom-0 w-[10px] h-[10px] rounded-full"
                  :class="[
                    chat.status === 'online' ? 'bg-success-500' : chat.status === 'away' ? 'bg-orange-400' : 'bg-gray-400',
                    'border-[2px] border-white dark:border-[#0c1427]'
                  ]"
                    ></span>
                  </div>
                  <div>
                    <span
                      class="block font-semibold text-black dark:text-white"
                    >
                  {{ chat.name || chat.phone }}
                    </span>
                <span class="block text-xs mt-[2px]" :class="{ 'text-success-600': chat.typing }">
                  {{ chat.typing ? directT('chat.typing') : chat.lastMessage }}
                    </span>
              </div>
            </div>
            <span class="text-xs block"> {{ chat.time }} </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import user31Image from "@/assets/images/users/user31.jpg";
import user6Image from "@/assets/images/users/user6.jpg";
import user7Image from "@/assets/images/users/user7.jpg";
import user8Image from "@/assets/images/users/user8.jpg";
import user9Image from "@/assets/images/users/user9.jpg";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";
import { supabase } from "@/utils/supabase";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

// הגדרת ממשק לצ'אט
interface ChatData {
  id: string | number;
  name?: string;
  phone?: string;
  profile_picture_url?: string;
  organization_id?: string | number;
  image?: string;
  lastMessage?: string;
  lastTime?: string;
  unread?: number;
  [key: string]: any; // לשדות נוספים שיכולים להיות
}

export default defineComponent({
  // eslint-disable-next-line vue/multi-word-component-names
  name: "Sidebar",
  emits: ['chat-selected', 'chats-loaded'],
  setup(props, { emit }) {
    // i18n setup
    const { locale } = useI18n();

    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };

    // Default profile image if none provided
    const defaultProfileImage = user31Image;
    
    // רשימת השיחות
    const chatList = ref<ChatData[]>([]);
    
    // פונקציה לטעינת הצ'אטים מהשרת
    const fetchChatThreads = async () => {
      console.log('fetchChatThreads: Starting to fetch chat threads');
      try {
        // Get user session to check if logged in
        const { data: sessionData } = await supabase.auth.getSession();
        const session = sessionData?.session;
        
        console.log('fetchChatThreads: Session check', { 
          isLoggedIn: !!session, 
          userId: session?.user?.id 
        });
        
        if (!session) {
          console.error('No active session');
          return;
        }
        
        // First, get the user's profile to get their organization_id
        console.log('fetchChatThreads: Getting user profile');
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', session.user.id)
          .single();
          
        if (profileError) {
          console.error('Error fetching user profile:', profileError);
          return;
        }
        
        if (!profileData || !profileData.org_id) {
          console.error('User profile has no organization_id');
          return;
        }
        
        console.log('fetchChatThreads: Got user profile', { 
          org_id: profileData.org_id 
        });
        
        // Use the new stored function to get threads with their last message in one query
        console.log('fetchChatThreads: Calling get_threads_with_last_message function');
        const { data, error } = await supabase
          .rpc('get_threads_with_last_message', { org_id: profileData.org_id });
          
        if (error) {
          console.error('Error fetching threads with last messages:', error);
          return;
        }
        
        console.log('fetchChatThreads: Received data from function', { 
          count: data?.length, 
          firstThread: data && data.length > 0 ? data[0] : null
        });
        
        // Create thread list from the results
        chatList.value = data ? data.map((thread: any) => ({
          id: thread.thread_id,
          name: thread.name || thread.phone,
          phone: thread.phone,
          profile_picture_url: thread.profile_picture_url,
          organization_id: thread.organization_id,
          image: thread.profile_picture_url || defaultProfileImage,
          lastMessage: formatLastMessage(thread.last_message, thread.last_message_type),
          lastTime: formatTimestamp(thread.last_message_time || thread.created_at),
          unread: thread.unread_count || 0,
          status: thread.status || 'offline',
          botstatus: thread.botstatus,
          assign_to: thread.assign_to
        })) : [];
        
        console.log('fetchChatThreads: Mapped chat data', { 
          chatListCount: chatList.value.length,
          firstChat: chatList.value.length > 0 ? chatList.value[0] : null
        });

        // Emit the chats-loaded event with the chat list
        if (chatList.value.length > 0) {
          emit('chats-loaded', chatList.value);
        }
        
      } catch (error) {
        console.error('Failed to fetch chat threads:', error);
        
        // Fallback to demo data if fetching fails
        console.log('fetchChatThreads: Using fallback demo data');
        chatList.value = [
          {
            id: 1,
            name: "Sarah Smith",
            image: user31Image,
            lastMessage: directT('chat.typing'),
            typing: true,
            lastTime: "1h",
            unread: 0
          },
          {
            id: 2,
            name: "Michael Johnson",
            image: user6Image,
            lastMessage: directT('chat.attachment'),
            typing: false,
            lastTime: "2h",
            unread: 3
          },
          {
            id: 3,
            name: "Emily Brown",
            image: user7Image,
            lastMessage: directT('chat.voiceMessage'),
            typing: false,
            lastTime: "4h",
            unread: 0
          },
          {
            id: 4,
            name: "David Martinez",
            image: user8Image,
            lastMessage: "Excellent work...",
            typing: false,
            lastTime: "8h",
            unread: 0
          },
          {
            id: 5,
            name: "Jessica Taylor",
            image: user9Image,
            lastMessage: "Hello! Mr.. 🔥",
            typing: false,
            lastTime: "1d",
            unread: 2
          }
        ];
        
        // Emit the chats-loaded event with the fallback chat list
        emit('chats-loaded', chatList.value);
      }
    };
    
    // Helper function to format last message based on its type
    const formatLastMessage = (message: string | null, messageType: string | null): string => {
      if (!message) return directT('chat.noMessages');
      
      if (messageType === 'image' || messageType === 'photo') {
        return '📷 ' + directT('chat.photo');
      } else if (messageType === 'audio' || messageType === 'voice') {
        return '🎤 ' + directT('chat.voiceMessage');
      } else {
        // Regular text message - limit to first few words
        return message.length > 30 
          ? message.substring(0, 27) + '...' 
          : message;
      }
    };
    
    // Format timestamp to relative time (e.g., "2h ago")
    const formatTimestamp = (timestamp: string): string => {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();
      const diffInMin = Math.floor(diffInMs / (1000 * 60));
      
      if (diffInMin < 1) return 'just now';
      if (diffInMin < 60) return `${diffInMin}m`;
      
      const diffInHours = Math.floor(diffInMin / 60);
      if (diffInHours < 24) return `${diffInHours}h`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return `${diffInDays}d`;
      
      return date.toLocaleDateString();
    };

    // בחירת צ'אט
    const selectChat = (chat: ChatData): void => {
      console.log('Sidebar: Chat selected', { 
        id: chat.id, 
        name: chat.name, 
        phone: chat.phone, 
        organization_id: chat.organization_id 
      });
      emit('chat-selected', chat);
    };
    
    // Load chat threads when component mounts
    onMounted(() => {
      fetchChatThreads();
    });

    return {
      chatList,
      selectChat,
      directT,
      defaultProfileImage
    };
  },
});
</script>

<style scoped>
.chat-sidebar-height {
  height: calc(100vh - 280px);
  max-height: 100%;
  overflow-y: auto;
}

@media (max-width: 768px) {
  .chat-sidebar-height {
    height: calc(100vh - 260px);
    max-height: 100%;
  }
}

:deep(.trezo-card) {
  max-height: 100vh;
  overflow: hidden;
  box-shadow: 0 10px 50px rgba(0,0,0,0.15) !important;
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.2)) !important;
}

:deep(.dark .trezo-card) {
  box-shadow: 0 10px 50px rgba(0,0,0,0.5) !important;
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.3)) !important;
}
</style>
